<script setup>
import Forms from '../forms/Forms.vue';

import { computed, onMounted, ref, nextTick, watch } from 'vue';
import TmsTable from '../table/TmsTable.vue';
import { xios } from '../forms/tempAxios'; // Assuming xios is set up

const columnsType = [
  {
    name: 'siNo',
    field: 'siNo', // Will be populated by index later
    label: 'SI. No.',
  },
  {
    name: 'bookingDate',
    field: 'bookingDate', // Assuming direct field
    label: 'Booking Date',
    sortable: true,
  },
  {
    name: 'plannedPickupDate',
    field: (row) =>
      row.Trips && row.Trips.length > 0
        ? row.Trips[0].plannedPickupDateTime
        : 'N/A', // Assuming from first Trip
    label: 'Planned Pickup Date',
    align: 'left',
    sortable: true,
  },
  {
    name: 'actualPickupDate',
    field: (row) =>
      row.Trips && row.Trips.length > 0
        ? row.Trips[0].actualPickupDateTime
        : 'N/A', // Assuming from first Trip
    label: 'Actual Pickup Date',
    sortable: true,
  },
  {
    name: 'plannedDeliveryDate',
    field: (row) =>
      row.Trips && row.Trips.length > 0
        ? row.Trips[0].plannedDeliveryDateTime
        : 'N/A', // Assuming from first Trip
    label: 'Planned Delivery Date',
    sortable: true,
  },
  {
    name: 'actualDeliveryDate',
    field: (row) =>
      row.Trips && row.Trips.length > 0
        ? row.Trips[0].actualDeliveryDateTime
        : 'N/A', // Assuming from first Trip
    label: 'Actual Delivery Date',
    sortable: true,
  },
  {
    name: 'bookingId',
    field: 'referenceNumber', // Assuming API returns 'referenceNumber' for Booking ID
    label: 'Booking ID',
    sortable: true,
  },
  {
    name: 'planner',
    field: 'plannerName', // Assuming a field 'plannerName' or from a related User model
    label: 'Planner',
    align: 'left',
    sortable: true,
  },
  {
    name: 'customerName',
    field: (row) => (row.Customer ? row.Customer.companyName : 'N/A'),
    label: 'Customer Name',
    sortable: true,
  },
  {
    name: 'subCustomer',
    field: (row) => (row.SubCustomer ? row.SubCustomer.companyName : 'N/A'), // Assuming a SubCustomer relation
    label: 'Sub-Customer',
    sortable: true,
  },
  {
    name: 'DON',
    field: 'doNumber', // Assuming 'doNumber'
    label: 'DO Number',
    sortable: true,
  },
  {
    name: 'bookingStatus',
    field: 'status', // Assuming 'status'
    label: 'Booking Status',
    sortable: true,
  },
  {
    name: 'bookingType',
    field: 'bookingType',
    label: 'Booking Type',
    sortable: true,
  },
  {
    name: 'orderType',
    field: 'orderType',
    label: 'Order Type',
    sortable: true,
  },
  {
    name: 'quantity',
    field: 'totalQuantity', // Assuming 'totalQuantity'
    label: 'Quantity',
    sortable: true,
  },
  {
    name: 'bookingSource',
    field: 'source', // Assuming 'source'
    label: 'Booking Source',
    sortable: true,
  },
  {
    name: 'driverName',
    field: (row) =>
      row.Trips && row.Trips.length > 0 && row.Trips[0].Driver
        ? row.Trips[0].Driver.name
        : 'N/A',
    label: 'Driver Name',
    sortable: true,
  },
  {
    name: 'driverPhone',
    field: (row) =>
      row.Trips && row.Trips.length > 0 && row.Trips[0].Driver
        ? row.Trips[0].Driver.phoneNumber
        : 'N/A',
    label: 'Driver Phone',
    sortable: true,
  },
  {
    name: 'truckNo',
    field: (row) =>
      row.Trips && row.Trips.length > 0 && row.Trips[0].Truck
        ? row.Trips[0].Truck.registrationNumber
        : 'N/A',
    label: 'Truck No',
    sortable: true,
  },
  {
    name: 'truckType',
    field: (row) =>
      row.Trips && row.Trips.length > 0 && row.Trips[0].Truck
        ? row.Trips[0].Truck.type
        : 'N/A',
    label: 'Truck Type',
    sortable: true,
  },
  {
    name: 'truckTonnage',
    field: (row) =>
      row.Trips && row.Trips.length > 0 && row.Trips[0].Truck
        ? row.Trips[0].Truck.tonnage
        : 'N/A', // Assuming tonnage on Truck
    label: 'Truck Tonnage',
    sortable: true,
  },
  {
    name: 'manPower',
    field: 'manpowerCount', // Assuming 'manpowerCount'
    label: 'Man Power',
    sortable: true,
  },
  {
    name: 'region',
    field: 'region',
    label: 'Region',
    sortable: true,
  },
  {
    name: 'collectionFrom',
    field: (row) =>
      row.Trips && row.Trips.length > 0 && row.Trips[0].CollectionPoint
        ? row.Trips[0].CollectionPoint.name
        : 'N/A', // Assuming from Trip.CollectionPoint
    label: 'Collection From',
    sortable: true,
  },
  {
    name: 'deliveryTo',
    field: (row) =>
      row.Trips && row.Trips.length > 0 && row.Trips[0].DeliveryPoint
        ? row.Trips[0].DeliveryPoint.name
        : 'N/A', // Assuming from Trip.DeliveryPoint
    label: 'Delivery To',
    sortable: true,
  },
  {
    name: 'totLocPoints',
    field: 'totalLocationPoints', // Assuming 'totalLocationPoints'
    label: 'Tot.Loc Points',
    sortable: true,
  },
  {
    name: 'pickupPoints',
    field: 'pickupPointsCount', // Assuming 'pickupPointsCount'
    label: 'Pickup Points',
    sortable: true,
  },
  {
    name: 'deliveryPoints',
    field: 'deliveryPointsCount', // Assuming 'deliveryPointsCount'
    label: 'Delivery Points',
    sortable: true,
  },
  // Removed: inProgress, completed, proofUploaded as these are likely status dependent or derived
  // They can be added back if specific fields exist or computed logic is clear
];

const columns = computed(() => {
  return columnsType.map((e) => ({
    ...e,
    align: e.align || 'left',
    sortable: e.sortable !== false,
  }));
});

// Removed static bookingStatus, orderType, region objects - these can be handled by q-table's cell slot if needed for styling

const data = ref([]);
const loading = ref(false);
const model = ref({}); // Holds filter values

const pagination = ref({
  sortBy: 'createdAt',
  descending: true,
  page: 1,
  rowsPerPage: 25,
  rowsNumber: 0, // Total number of rows from server
});

async function fetchData(
  page = pagination.value.page,
  rowsPerPage = pagination.value.rowsPerPage,
  sortBy = pagination.value.sortBy,
  descending = pagination.value.descending,
) {
  loading.value = true;
  try {
    const filterFields = [
      {
        field: 'clientId', // Assuming clientId is always required
        operator: 'eq',
        value: JSON.parse(localStorage.getItem('userInfo')).uid,
      },
    ];

    // Dynamically add filters based on model values
    if (model.value.customerId) {
      filterFields.push({
        field: 'customerId',
        operator: 'eq',
        value: model.value.customerId,
      });
    }
    if (model.value.subCustomerId) {
      filterFields.push({
        field: 'subCustomerId',
        operator: 'eq',
        value: model.value.subCustomerId,
      });
    }
    if (model.value.vendorId) {
      // Assuming a vendorId field on Booking
      filterFields.push({
        field: 'vendorId',
        operator: 'eq',
        value: model.value.vendorId,
      });
    }
    // For Driver, Truck, Supervisor, Region - these might be on the Trip or Booking
    // For simplicity, let's assume direct filters on Booking for now or via Trip association
    // This part may need adjustment based on your exact data model and how these are linked.
    // For example, if filtering by driverName, the backend needs to support filtering Bookings based on associated Trip's Driver's name.

    // Example for region if it's a direct field on Booking:
    if (model.value.region) {
      filterFields.push({
        field: 'region',
        operator: 'eq',
        value: model.value.region,
      });
    }
    // Add more filters here based on `model.value` for driver, truck, supervisor etc.
    // This might involve more complex query structures if these are on related models (e.g. Trip.driverId)

    const apiPayload = {
      model: 'Booking',
      page: page,
      itemsPerPage: rowsPerPage,
      attributesInclude: [], // Specify if only certain attributes are needed
      includeModels: [
        { model: 'Customer', attributes: ['companyName'] },
        { model: 'SubCustomer', attributes: ['companyName'] }, // If SubCustomer is a separate related model
        {
          model: 'Trip',
          // attributes: ['plannedPickupDateTime', 'actualPickupDateTime', ...], // Specify needed trip attributes
          includeModels: [
            { model: 'Driver', attributes: ['name', 'phoneNumber'] },
            {
              model: 'Truck',
              attributes: ['registrationNumber', 'type', 'tonnage'],
            },
            { model: 'CollectionPoint', attributes: ['name'] },
            { model: 'DeliveryPoint', attributes: ['name'] },
          ],
        },
        // Add other primary related models if necessary, e.g., Vendor
      ],
      filterFields: filterFields,
      // search: search.value // If you implement a global search
    };

    const res = await xios.post(
      'api-tms/script-pagination-standard',
      apiPayload,
    );

    if (res.data.success) {
      console.log('API Response Data:', res.data); // Log the response data
      data.value = res.data.data.items.map((item, index) => ({
        ...item,
        siNo: (page - 1) * rowsPerPage + index + 1,
        // Ensure nested data is correctly accessed or mapped if not done by column field functions
        customerName: (item.Customer && item.Customer.companyName) || 'N/A',
        // driverName, truckNo, etc. will be handled by column field functions
      }));
      pagination.value.rowsNumber = res.data.data.totalItems; // This still uses totalItems, ensure this is correct for grand total
      // Update Total Bookings card
      if (
        cards.value &&
        cards.value.length > 0 &&
        cards.value[0].label === 'Total Bookings'
      ) {
        cards.value[0].value = res.data.data.items
          ? res.data.data.items.length
          : 0; // Use length of items array for the card
      }
    } else {
      console.error('Failed to fetch dashboard data:', res.data);
      data.value = [];
      pagination.value.rowsNumber = 0;
      // Reset Total Bookings card on failure
      if (
        cards.value &&
        cards.value.length > 0 &&
        cards.value[0].label === 'Total Bookings'
      ) {
        cards.value[0].value = 0;
      }
    }
  } catch (error) {
    console.error('Error fetching dashboard data:', error);
    data.value = [];
    pagination.value.rowsNumber = 0;
    // Reset Total Bookings card on error
    if (
      cards.value &&
      cards.value.length > 0 &&
      cards.value[0].label === 'Total Bookings'
    ) {
      cards.value[0].value = 0;
    }
  } finally {
    loading.value = false;
  }
}

function handleRequest(props) {
  const { page, rowsPerPage, sortBy, descending } = props.pagination;
  pagination.value.page = page;
  pagination.value.rowsPerPage = rowsPerPage;
  pagination.value.sortBy = sortBy;
  pagination.value.descending = descending;
  fetchData(page, rowsPerPage, sortBy, descending);
}

onMounted(async () => {
  await nextTick();
  await fetchData(); // Initial data fetch
});

// Watch for changes in filters and refetch
watch(
  model,
  () => {
    pagination.value.page = 1; // Reset to first page on filter change
    fetchData();
  },
  { deep: true },
);

const search = ref(''); // Kept if you want to add a search bar to TmsTable

// Update the `create` array with more specific model keys for clarity
const create = [
  {
    is: 'Row',
    ui: [
      {
        is: 'TSelect',
        icon: 'search',
        Label: 'Choose Customer*',
        options: [], // Options should be fetched or be dynamic
        'emit-value': true,
        'map-options': true, // If options are objects {label, value}
        optionLabel: 'name',
        optionValue: 'id', // Adjust if options are fetched
        clearable: true,
        'use-input': true, // For server-side filtering of options if TSelect supports it
        model: 'customerId', // Changed from 'customer'
      },
      {
        is: 'TSelect',
        Label: 'Choose Sub-Customer',
        icon: 'search',
        options: [],
        'emit-value': true,
        'map-options': true,
        optionLabel: 'name',
        optionValue: 'id',
        disable: computed(() => !model.value.customerId), // Example: enable if customer is chosen
        model: 'subCustomerId', // Changed from 'subCustomer'
      },
      {
        is: 'TSelect',
        icon: 'search',
        Label: 'Choose Vendor*',
        options: [],
        'emit-value': true,
        'map-options': true,
        optionLabel: 'name',
        optionValue: 'id',
        clearable: true,
        'use-input': true,
        model: 'vendorId', // Changed from 'customer'
      },
      {
        is: 'TSelect',
        icon: 'search',
        Label: 'Choose Driver Name',
        options: [],
        'emit-value': true,
        'map-options': true,
        optionLabel: 'name',
        optionValue: 'id', // Assuming driver options are {name, id}
        clearable: true,
        'use-input': true,
        model: 'driverId', // Changed, assuming this filters by Trip.driverId
      },
    ],
  },
  {
    is: 'Row',
    ui: [
      {
        is: 'TSelect',
        icon: 'search',
        Label: 'Supervisor*',
        options: [],
        'emit-value': true,
        'map-options': true,
        optionLabel: 'name',
        optionValue: 'id',
        clearable: true,
        'use-input': true,
        model: 'supervisorId', // Changed
      },
      {
        is: 'TSelect',
        icon: 'search',
        Label: 'Truck Number',
        options: [],
        'emit-value': true,
        'map-options': true,
        optionLabel: 'registrationNumber',
        optionValue: 'id', // Assuming truck options
        clearable: true,
        'use-input': true,
        model: 'truckId', // Changed from 'subCustomer'
      },
      {
        is: 'TSelect',
        icon: 'search',
        Label: 'Truck Tonnage Type', // This might be a text input or a select with predefined tonnages
        options: ['1 Ton', '3 Ton', '5 Ton', '10 Ton'], // Example options
        clearable: true,
        // 'use-input': true, // Only if options are many/dynamic
        model: 'truckTonnage', // Changed
      },
      {
        is: 'TSelect',
        icon: 'search',
        Label: 'Select Region',
        options: [], // Should be fetched or predefined list of regions
        'emit-value': true,
        'map-options': true,
        optionLabel: 'name',
        optionValue: 'name', // Or 'id' if regions have IDs
        clearable: true,
        'use-input': true,
        model: 'region', // Changed
      },
    ],
  },
];
// const model = {}; // model is now a ref

const cards = ref([
  // Made cards reactive if their values need to be updated from API
  { label: 'Total Bookings', value: 0 },
  { label: 'On-Time Bookings', value: 0 },
  { label: 'Delayed Bookings', value: 0 },
  { label: 'Total Distance', value: '0 km' },
]);
</script>
<template>
  <div class="fit bg- flex flex-col gap-4">
    <q-expansion-item
      expand-separator
      default-opened
      label="Filter"
      class="p-2"
      header-class="text-lg font-semibold"
    >
      <div class="flex flex-col gap-2">
        <Forms
          :ui="create"
          @update:modelValue="(val) => (model = val)"
          :vmodel="model"
        />
        <q-btn
          color="primary"
          icon="search"
          label="Fetch Data"
          @click="
            () => {
              pagination.page = 1;
              fetchData();
            }
          "
          noCaps
        />
      </div>
    </q-expansion-item>

    <div class="flex w-full gap-2 p-4">
      <q-card class="col flex q-pa-md gap-4" v-for="n in cards">
        <div class="flex flex-center w-1/5">
          <q-btn outline color="primary" icon="task" class="text-md" round />
        </div>
        <q-separator vertical />
        <div class="flex flex-col flex-1 gap-2">
          <div class="font-semibold text-2xl">{{ n.label }}</div>
          <q-separator />
          <div class="font-semibold text-2xl">{{ n.value }}</div>
        </div>
      </q-card>
    </div>
    <div class="flex flex-1">
      <TmsTable
        :data="data"
        selected="Booking & Tracking"
        :columns="columns"
        row-key="id"
        :loading="loading"
        v-model:pagination="pagination"
        @request="handleRequest"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped></style>
